import React, {FC, useEffect} from 'react';
import styled from 'styled-components';
import {useComponent} from '../component-library';
import scrollIntoView from 'scroll-into-view-if-needed';

export interface GraphicRadioButtonProps {
  value: string;
  iconMobile?: JSX.Element;
  iconDesktop?: JSX.Element;
  content: string;
  selected: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name: string;
  questionTextId: string;
  disabled?: boolean;
  parentScrollToRef?: React.RefObject<HTMLDivElement>;
  testId?: string;
}

export const GraphicRadioButton: FC<GraphicRadioButtonProps> = (props) => {
  const {
    iconMobile,
    iconDesktop,
    content,
    selected,
    onChange,
    value,
    name,
    questionTextId,
    disabled = false,
    parentScrollToRef,
    testId,
  } = props;

  const Text = useComponent('Text');
  const scrollToRef = React.useRef<HTMLLabelElement>(null);

  useEffect(() => {
    if (scrollToRef.current && selected) {
      scrollIntoView(scrollToRef.current, {
        behavior: 'smooth',
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'center',
        boundary: parentScrollToRef && parentScrollToRef.current,
      });
    }
  }, [selected]);

  const inputId = `graphic-radio-button-input--${value}`;

  return (
    <>
      <HiddenRadioButton
        type="radio"
        onChange={onChange}
        value={value}
        checked={selected && !disabled}
        disabled={disabled}
        name={name}
        aria-describedby={questionTextId}
        id={inputId}
      />
      <GraphicRadioButtonWrapper
        disabled={disabled}
        selected={selected && !disabled}
        hasIcon={iconMobile !== undefined || iconDesktop !== undefined}
        ref={scrollToRef}
        htmlFor={inputId}
        data-testid={testId}
      >
        <ContentWrapper>
          {iconMobile && <IconMobileWrapper>{iconMobile}</IconMobileWrapper>}
          {iconDesktop && (
            <IconDesktopWrapper>{iconDesktop}</IconDesktopWrapper>
          )}
          <TextWrapper>
            <Text tag="p" variant="label-small">
              {content}
            </Text>
          </TextWrapper>
        </ContentWrapper>
      </GraphicRadioButtonWrapper>
    </>
  );
};

const HiddenRadioButton = styled.input`
  position: absolute;
  opacity: 0;
  cursor: inherit;
`;

const GraphicRadioButtonWrapper = styled.label<{
  disabled: boolean;
  selected: boolean;
  hasIcon: boolean;
  ref: React.RefObject<HTMLLabelElement>;
  htmlFor: string;
}>`
  display: inline-flex;
  position: relative;
  flex-basis: 0;
  flex-direction: column;
  flex-grow: 1;
  align-items: ${(props) => (props.hasIcon ? 'baseline' : 'center')};
  justify-content: center;
  padding: ${(props) =>
    props.hasIcon
      ? props.theme.brand.elements.inputArea.genericChoiceButton.padding.mobile
          .withIcon
      : props.theme.brand.elements.inputArea.genericChoiceButton.padding.mobile
          .withoutIcon};
  border-radius: ${(props) =>
    props.theme.brand.elements.inputArea.genericChoiceButton.border.radius
      .default};
  background-color: ${(props) =>
    props.selected
      ? props.theme.brand.elements.inputArea.genericChoiceButton.backgroundColor
          .selected
      : 'transparent'};
  box-shadow: inset 0 0 0
    ${(props) =>
      props.selected
        ? props.theme.brand.elements.inputArea.genericChoiceButton.border.width
            .selected
        : props.theme.brand.elements.inputArea.genericChoiceButton.border.width
            .default};
  color: ${(props) =>
    props.selected
      ? props.theme.brand.elements.inputArea.genericChoiceButton.color.selected
      : props.disabled
      ? props.theme.brand.elements.inputArea.genericChoiceButton.color.disabled
      : props.theme.brand.elements.inputArea.genericChoiceButton.color.default};
  white-space: nowrap;
  hyphens: auto;
  cursor: pointer;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  overflow-wrap: anywhere;

  /* Apply color to paragraph and icon */
  p,
  svg {
    color: inherit;
  }

  & + ${HiddenRadioButton} + & {
    margin-left: ${(props) => props.theme.internal.size.static150};
  }

  @media (min-width: ${(props) => props.theme.internal.Breakpoints.b560}px) {
    display: flex;
    align-items: center;
    padding: ${(props) =>
      props.hasIcon
        ? props.theme.brand.elements.inputArea.genericChoiceButton.padding
            .desktop.withIcon
        : props.theme.brand.elements.inputArea.genericChoiceButton.padding
            .desktop.withoutIcon};
    text-align: center;
    white-space: normal;

    & + & {
      margin-left: 0;
    }
  }

  &:hover {
    /* color is used to override the "box-shadow" attribute, so using "border" is correct */
    color: ${(props) =>
      props.theme.brand.elements.inputArea.genericChoiceButton.color.hover};
  }

  ${HiddenRadioButton}:focus + & {
    border-radius: ${(props) =>
      props.theme.brand.elements.inputArea.genericChoiceButton.border.radius
        .focus};
    outline: solid
      ${(props) =>
        props.theme.brand.elements.inputArea.genericChoiceButton.color.focus};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.internal.size.static100};

  @media (min-width: ${(props) => props.theme.internal.Breakpoints.b560}px) {
    display: block;
    align-items: initial;
    gap: ${(props) => props.theme.internal.size.static200};
  }
`;

const TextWrapper = styled.div`
  p {
    margin: 0;
  }
`;

const IconMobileWrapper = styled.div`
  @media (min-width: ${(props) => props.theme.internal.Breakpoints.b560}px) {
    display: none;
  }
`;

const IconDesktopWrapper = styled.div`
  display: none;

  @media (min-width: ${(props) => props.theme.internal.Breakpoints.b560}px) {
    display: inline-block;
  }
`;
